import React from 'react';
import { Download, File as FileIcon, Music as AudioIcon, Play } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { formatBytes, formatDuration, type AudioContent, type FileContent, type ImageContent, type VideoContent } from './shared';
import type { ArtifactViewerProps } from '../types';

export const FileViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<FileContent>;
  const name = typeof c?.name === 'string' ? c.name : 'file';
  const mimeType = typeof c?.mimeType === 'string' ? c.mimeType : 'application/octet-stream';
  const url = typeof c?.url === 'string' ? c.url : undefined;
  const size = typeof c?.size === 'number' ? c.size : undefined;
  const preview = typeof c?.preview === 'string' ? c.preview : undefined;
  const isPdf = mimeType.includes('pdf') || /\.pdf$/i.test(name);
  const isImage = mimeType.startsWith('image/');
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
          <FileIcon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground truncate">{name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {mimeType}{size ? ` · ${formatBytes(size)}` : ''}
          </div>
          {url && (
            <div className="mt-3 flex items-center gap-2">
              <Button asChild size="sm" variant="default" className="h-7 text-xs">
                <a href={url} target="_blank" rel="noreferrer">Apri</a>
              </Button>
              <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                <a href={url} download={name} target="_blank" rel="noreferrer">
                  <Download className="h-3 w-3 mr-1" /> Scarica
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>
      {url && isImage && (
        <div className="mt-3 bg-muted rounded-lg overflow-hidden border border-border">
          <img src={url} alt={name} className="w-full h-auto max-h-[480px] object-contain" loading="lazy" />
        </div>
      )}
      {url && isPdf && (
        <iframe src={url} title={name} className="mt-3 w-full h-[480px] rounded-lg border border-border bg-card" />
      )}
      {!isPdf && !isImage && preview && (
        <div className="mt-3 p-2 bg-muted rounded text-xs text-foreground whitespace-pre-wrap font-mono max-h-48 overflow-auto">
          {preview}
        </div>
      )}
    </div>
  );
};

export const ImageViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<ImageContent>;
  const url = typeof c?.url === 'string' ? c.url : '';
  const alt = typeof c?.alt === 'string' ? c.alt : artifact.title;
  if (!url) return <div className="p-4 text-sm text-muted-foreground">Immagine non disponibile</div>;
  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border">
      <img
        src={url}
        alt={alt}
        className="w-full h-auto max-h-[480px] object-contain bg-muted"
        loading="lazy"
      />
      {(c?.width || c?.height || alt) && (
        <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border flex items-center justify-between">
          <span className="truncate">{alt}</span>
          {(c?.width && c?.height) && <span>{c.width} × {c.height}</span>}
        </div>
      )}
    </div>
  );
};

export const VideoViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<VideoContent>;
  const url = typeof c?.url === 'string' ? c.url : '';
  const poster = typeof c?.poster === 'string' ? c.poster : undefined;
  if (!url) return <div className="p-4 text-sm text-muted-foreground">Video non disponibile</div>;
  return (
    <div className="bg-black rounded-lg overflow-hidden">
      <video src={url} poster={poster} controls className="w-full max-h-[480px]" preload="metadata" />
      {typeof c?.durationSec === 'number' && (
        <div className="px-3 py-2 text-xs text-muted-foreground bg-slate-900 flex items-center gap-2">
          <Play className="h-3 w-3" /> {formatDuration(c.durationSec)}
        </div>
      )}
    </div>
  );
};

export const AudioPlayerViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<AudioContent>;
  const url = typeof c?.url === 'string' ? c.url : '';
  const transcript = typeof c?.transcript === 'string' ? c.transcript : undefined;
  if (!url) return <div className="p-4 text-sm text-muted-foreground">Audio non disponibile</div>;
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
          <AudioIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground truncate">{artifact.title}</div>
          {typeof c?.durationSec === 'number' && (
            <div className="text-xs text-muted-foreground">{formatDuration(c.durationSec)}</div>
          )}
        </div>
      </div>
      <audio src={url} controls className="w-full" preload="metadata" />
      {transcript && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-muted-foreground font-medium">Trascrizione</summary>
          <div className="mt-2 p-2 bg-muted rounded text-foreground whitespace-pre-wrap max-h-48 overflow-auto">
            {transcript}
          </div>
        </details>
      )}
    </div>
  );
};
